import { useEffect, useRef, useState } from "react";
import { Archive, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/Common";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

export const FilesPanel = ({ resourceType, resourceId, canWrite }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = () => api.get("/files", { params: { resource_type: resourceType, resource_id: resourceId } }).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, [resourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("resource_type", resourceType);
    fd.append("resource_id", resourceId);
    setBusy(true);
    try { await api.post("/files", fd); toast.success("Archivo subido"); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const open = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const archive = async (f) => {
    try { await api.post(`/files/${f.id}/archive`); toast.success("Archivo archivado"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="syc-card overflow-hidden" data-testid="files-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e4e8e1]">
        <span className="font-semibold text-sm">Archivos ({files.length})</span>
        {canWrite && <>
          <input ref={input} type="file" className="hidden" onChange={upload} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" data-testid="file-input" />
          <Button data-testid="upload-file-btn" size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => input.current?.click()}><Upload size={14} className="mr-1" />{busy ? "Subiendo…" : "Subir"}</Button>
        </>}
      </div>
      {files.length === 0 ? <EmptyState title="Sin archivos" text="PDF, imágenes, Word o Excel hasta 10 MB." /> : files.map((f) => (
        <div key={f.id} className="flex items-center gap-3 px-5 py-2.5 border-b border-[#f0f2ee] last:border-0 text-sm" data-testid={`file-row-${f.id}`}>
          <FileText size={16} className="text-[#258F00] shrink-0" />
          <button className="flex-1 min-w-0 text-left truncate font-medium hover:underline" onClick={() => open(f)} data-testid={`file-open-${f.id}`}>{f.original_name}</button>
          <span className="text-xs text-[#8a9584] hidden sm:block">{Math.ceil(f.size / 1024)} KB · {f.uploaded_by_name} · {fmtDateTime(f.created_at)}</span>
          {canWrite && <button title="Archivar" onClick={() => archive(f)} data-testid={`file-archive-${f.id}`} className="text-[#8a9584] hover:text-[#b42318]"><Archive size={15} /></button>}
        </div>
      ))}
    </div>
  );
};

export const StatusTimeline = ({ items, labels = {} }) => (
  <div className="syc-card p-5" data-testid="status-timeline">
    <div className="font-semibold text-sm mb-3">Historial de estados</div>
    <ol className="space-y-3">
      {items.map((h, i) => (
        <li key={i} className="flex gap-3 text-sm">
          <span className="mt-1.5 h-2 w-2 rounded-full bg-[#5CD500] shrink-0" />
          <div><b>{labels[h.from] || h.from || "—"} → {labels[h.to] || h.to}</b> · {h.user_name}
            <div className="text-xs text-[#8a9584]">{fmtDateTime(h.ts)}{h.reason ? ` · ${h.reason}` : ""}{h.note ? ` · ${h.note}` : ""}</div></div>
        </li>
      ))}
    </ol>
  </div>
);
